// Calendar dates in the browser, as 'YYYY-MM-DD' text.
//
// Same reasoning as the server's lib/dates.ts: a booking is on the 17th, and
// turning that into a JavaScript Date to do arithmetic is how the 17th becomes
// the 16th at 11pm. Everything here goes string in, string out.

export type DateOnly = string;

export function todayISO(): DateOnly {
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function addDays(date: DateOnly, days: number): DateOnly {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export function addMonths(date: DateOnly, months: number): DateOnly {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  // Day 1 first, so adding a month to the 31st does not skip one.
  const shifted = new Date(Date.UTC(y, m - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 0)
  ).getUTCDate();
  shifted.setUTCDate(Math.min(d, lastDay));
  return shifted.toISOString().slice(0, 10);
}

export function startOfMonth(date: DateOnly): DateOnly {
  return `${date.slice(0, 7)}-01`;
}

export function endOfMonth(date: DateOnly): DateOnly {
  const [y, m] = date.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

/** Monday-first, which is how a working week reads. */
export function startOfWeek(date: DateOnly): DateOnly {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  const backToMonday = day === 0 ? 6 : day - 1;
  return addDays(date, -backToMonday);
}

/** The 6×7 grid a month view draws, including the neighbouring days. */
export function monthGrid(anchor: DateOnly): DateOnly[] {
  const first = startOfWeek(startOfMonth(anchor));
  return Array.from({ length: 42 }, (_, i) => addDays(first, i));
}

export function isWeekend(date: DateOnly): boolean {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return day === 0 || day === 6;
}

export function sameMonth(a: DateOnly, b: DateOnly): boolean {
  return a.slice(0, 7) === b.slice(0, 7);
}

export function dayOfMonth(date: DateOnly): number {
  return Number(date.slice(8, 10));
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export function monthLabel(date: DateOnly): string {
  return `${MONTHS[Number(date.slice(5, 7)) - 1]} ${date.slice(0, 4)}`;
}

/**
 * '2026-10-13' → '13/10/2026'. THE way a date reads anywhere a customer sees
 * one (round 45, Nathan: "1 Nov" hid the year and looked unprofessional).
 * String in, string out: no Date, so no timezone can move the day. Display
 * only: `<input type="date">` values, sort keys and CSV stay ISO.
 */
export function formatDate(date: DateOnly): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}/${date.slice(0, 4)}`;
}

/** '13/10/2026', or '13/10/2026 to 15/10/2026' for a range. */
export function dateRangeLabel(from: DateOnly, to: DateOnly): string {
  if (from === to) return formatDate(from);
  return `${formatDate(from)} to ${formatDate(to)}`;
}

/**
 * The same as formatDate. Round 37 introduced it (07-03-2026) for dates that
 * leaked raw ISO; round 45 moved every date to slashes, and the name stays so
 * its callers did not all have to change in the same commit.
 */
export function formatDMY(date: DateOnly): string {
  return formatDate(date);
}

/**
 * Today written out, 'Tuesday 13 October 2026': Home's heading under the
 * greeting (Zanetto Tools' dashboard line). A heading, with the year in it;
 * every other date is formatDate.
 */
export function longDayHeading(date: DateOnly): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return `${names[weekday]} ${d} ${MONTHS[m - 1]} ${y}`;
}

export function timeRangeLabel(start: string | null, end: string | null): string | null {
  if (!start || !end) return null;
  return `${start}–${end}`;
}

export function eachDate(from: DateOnly, to: DateOnly): DateOnly[] {
  const out: DateOnly[] = [];
  for (let cursor = from; cursor <= to; cursor = addDays(cursor, 1)) out.push(cursor);
  return out;
}

export const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Sunday, for a Monday-first week. */
export function endOfWeek(date: DateOnly): DateOnly {
  return addDays(startOfWeek(date), 6);
}

/** 'Mon' — the weekday of a specific date, for timeline column headings. */
export function weekdayShort(date: DateOnly): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return WEEKDAY_LABELS[day === 0 ? 6 : day - 1]!;
}

/**
 * Do two inclusive date ranges touch?
 *
 * Inclusive on both ends, because a booking that ends on the 19th occupies the
 * 19th. Note this is deliberately NOT the clash rule: Dayboard's schedule
 * treats touching bookings as fine (a release frees today onward, and
 * back-to-back boundaries are not a conflict). This is only "does this bar
 * appear in this window", which is a drawing question, not a booking one.
 */
export function rangesOverlap(
  aFrom: DateOnly,
  aTo: DateOnly,
  bFrom: DateOnly,
  bTo: DateOnly
): boolean {
  return aFrom <= bTo && bFrom <= aTo;
}

/** How many days from `from` to `to`, inclusive. Always at least 1. */
export function daysInclusive(from: DateOnly, to: DateOnly): number {
  if (to < from) return 1;
  let n = 1;
  for (let cursor = from; cursor < to; cursor = addDays(cursor, 1)) n++;
  return n;
}

/**
 * A clock time the way Zanetto Tools writes one: `7:00am` — no leading zero,
 * lowercase meridiem, no space. Adopted for every schedule surface in parity
 * pass 3 (99-decisions round 22) so a manager moving between the two products
 * reads one format. `<input type="time">` keeps 24h — that is the control's
 * format, not the display's.
 */
export function formatClock(time: string | null): string | null {
  if (!time) return null;
  const hours = Number(time.slice(0, 2));
  const minutes = time.slice(3, 5);
  const meridiem = hours < 12 ? 'am' : 'pm';
  const clock = hours % 12 === 0 ? 12 : hours % 12;
  return `${clock}:${minutes}${meridiem}`;
}

/** The pair, or null when either half is missing — callers render "All day". */
export function formatClockRange(start: string | null, end: string | null): string | null {
  const from = formatClock(start);
  const to = formatClock(end);
  if (!from || !to) return null;
  return `${from} – ${to}`;
}

/**
 * "Nathan Zanetto" → "N Zanetto". Zanetto Tools' space-saver for crew names in
 * dense schedule cells; a single-word name is left alone.
 */
export function compactName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  return `${parts[0]![0]} ${parts.slice(1).join(' ')}`;
}

/**
 * An instant as a person reads it: "30/09/2026, 6:45 am". For the moments a
 * form was completed or saved on a phone (round 42), where the time of day is
 * the point.
 */
export function formatMomentAu(iso: string): string {
  return new Date(iso).toLocaleString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Whether a record should state the phone's completion time beside the time
 * Dayboard received it (round 42). The server's own threshold
 * (`DEVICE_TIME_SHOWN_GAP_MS` in server/src/lib/deviceTime.ts): a form sent
 * with a moment's delay reads as one time.
 */
export function deviceTimeWorthStating(
  device: string | null | undefined,
  received: string | null | undefined
): boolean {
  if (!device || !received) return false;
  return Date.parse(received) - Date.parse(device) >= 5 * 60 * 1000;
}
